import React, { useState, useMemo, useCallback } from "react";
import { Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon } from "@hugeicons/core-free-icons";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useSystem } from "@/utils/hooks/useLocation";
import ConfirmModal from "@/CommonElements/ConfirmModal";
import {
  useReportWithHouseQuery,
  useUpdateIncidentReportMutation,
} from "@/services/reports";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import dayjs from "dayjs";

const formatLookupName = (obj) =>
  obj?.name_en ?? obj?.name_ar ?? obj?.name ?? obj?.code ?? "—";
const emptyCell = "—";

const formatReportDateTime = (date) =>
  date ? dayjs(date).format("A hh:mm YYYY-MM-DD") : emptyCell;

const PAGE_SIZE = 10;

// Column order as in design: Report Number, Pilgrim #, Name, Nationality, Campaign/Group, Service Center, Housing Contract, Case Description, Report Date, Actions
const DEFAULT_VISIBLE_COLUMN_KEYS = [
  "incident_number",
  "pilgrim_number",
  "pilgrim_name",
  "nationality",
  "campaign_number",
  "service_center",
  "housing_contract",
  "description",
  "reported_at",
  "actions",
];

const BuildingsNotInspected = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedText } = useLocalizedText();
  const { currentSystem } = useSystem();
  const system = currentSystem || "makkah";
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [endCaseModalState, setEndCaseModalState] = useState({
    isOpen: false,
    reportId: null,
  });

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
      // Optional: show only unresolved reports (open cases) for housing
      ...(filters.resolved_at !== undefined && filters.resolved_at !== ""
        ? { resolved_at: filters.resolved_at }
        : {}),
      ...Object.fromEntries(
        Object.entries(filters).filter(
          ([key, value]) =>
            key !== "resolved_at" &&
            value !== "" &&
            value !== null &&
            value !== undefined
        )
      ),
    }),
    [currentPage, debouncedValue, filters]
  );

  const { data, isLoading, isFetching } = useReportWithHouseQuery(
    queryParams,
    { system }
  );
  const updateReport = useUpdateIncidentReportMutation({ system });

  const reports = data?.data?.data ?? data?.data ?? [];
  const meta = data?.data?.meta ?? data?.meta ?? {};
  const total = meta?.total ?? 0;
  const loading = isLoading || isFetching;

  const handleSearchChange = (e) => {
    setSearchValue(e?.target?.value ?? "");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleEndCaseClick = useCallback((reportId) => {
    setEndCaseModalState({ isOpen: true, reportId });
  }, []);

  const handleEndCaseCancel = () => {
    setEndCaseModalState({ isOpen: false, reportId: null });
  };

  const handleEndCaseConfirm = () => {
    const reportId = endCaseModalState.reportId;
    if (!reportId) {
      handleEndCaseCancel();
      return;
    }
    updateReport.mutate(
      {
        id: reportId,
        resolved_at: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          setEndCaseModalState({ isOpen: false, reportId: null });
        },
      }
    );
  };

  const getActions = useCallback(
    (rowData) => [
      {
        label: (
          <Link
            to={{
              pathname: `/app/${currentSystem}/reports/${rowData.id}/view`,
              state: { fromBuildingsNotInspected: true },
            }}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={18} icon={ViewIcon} />
            <span>{t("VIEW_REPORT")}</span>
          </Link>
        ),
        key: "view",
      },
      { type: "divider" },
      {
        label: (
          <div
            className="d-flex align-items-center gap-2"
            role="button"
            tabIndex={0}
            onClick={() =>{
              navigate(
                `/app/${currentSystem}/housing-inspection-orders/create`,
                {
                  state: {
                    fromReportId: rowData.id,
                    contractId: rowData.contract_id ?? rowData.pilgrim?.contract_id,
                    fromBuildingsNotInspected: true,
                    houseComrRegNo: rowData?.house?.houseComrRegNo,
                  },
                }
              )}
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(
                  `/app/${currentSystem}/housing-inspection-orders/create`,
                  { state: { fromReportId: rowData.id, contractId: rowData.contract_id ?? rowData.pilgrim?.contract_id } }
                );
              }
            }}
          >
            <span>{t("CREATE_HOUSING_INSPECTION")}</span>
          </div>
        ),
        key: "create_inspection",
      },
      { type: "divider" },
      {
        label: (
          <div className="d-flex align-items-center gap-2">
            <span>{t("END_CASE")}</span>
          </div>
        ),
        key: "end_case",
        onClick: () => handleEndCaseClick(rowData.id),
        danger: false,
      },
    ],
    [currentSystem, t, navigate, handleEndCaseClick]
  );

  const contractId = (row) =>
    row?.contract_id ?? row?.pilgrim?.contract_id ?? null;

  const columns = useMemo(
    () => [
      {
        name: t("REPORT_NUMBER"),
        key: "incident_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("incident_number"),
        filterable: true,
        render: (value, rowData) => {
          const num = value ?? rowData?.id ?? emptyCell;
          return (
            <Link
              className="fw-bold text-decoration-none"
              to={{
                pathname: `/app/${currentSystem}/reports/${rowData?.id}/view`,
                state: { fromBuildingsNotInspected: true },
              }}
            >
              {num}
            </Link>
          );
        },
      },
      {
        name: t("PILGRIM_NUMBER"),
        key: "pilgrim_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("pilgrim_number"),
        filterable: true,
        render: (_, rowData) =>
          rowData?.pilgrim?.border_number ?? emptyCell,
      },
      {
        name: t("PILGRIM_NAME"),
        key: "pilgrim_name",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("pilgrim_name"),
        filterable: true,
        render: (_, rowData) =>
          (rowData?.reported_person_name ||
            getLocalizedText(
              rowData?.pilgrim?.full_name_ar,
              rowData?.pilgrim?.full_name_en
            )) ?? emptyCell,
      },
      {
        name: t("NATIONALITY"),
        key: "nationality",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("nationality"),
        filterable: true,
        render: (_, rowData) =>
          getLocalizedText(
            rowData?.pilgrim?.nationality_ar,
            rowData?.pilgrim?.nationality_en
          ) ?? emptyCell,
      },
      {
        name: t("CAMPAIGN_OR_GROUP_NUMBER"),
        key: "campaign_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("campaign_number"),
        filterable: true,
        render: (_, rowData) =>
          rowData?.pilgrim?.campaign_number ?? rowData?.campaign_number ?? emptyCell,
      },
      {
        name: t("SERVICE_CENTER"),
        key: "service_center",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("service_center"),
        filterable: true,
        render: (value, rowData) =>
          (value && typeof value === "object"
            ? formatLookupName(value)
            : value) ??
          formatLookupName(rowData?.pilgrim?.service_center) ??
          emptyCell,
      },
      {
        name: t("HOUSING_CONTRACT"),
        key: "housing_contract",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("housing_contract"),
        filterable: true,
        render: (_, rowData) => {
          const cId = contractId(rowData);
          if (!cId) return emptyCell;
          return (
            <span className="d-inline-flex align-items-center gap-1">
              <Link
                to={`/app/${currentSystem}/housing-contracts/${cId}/view`}
                className="text-decoration-none d-inline-flex align-items-center gap-1"
              >
                {cId}
                <HugeiconsIcon icon={ViewIcon} size={16} className="text-secondary" />
              </Link>
            </span>
          );
        },
      },
      {
        name: t("CASE_DESCRIPTION"),
        key: "description",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("description"),
        filterable: true,
        render: (value) =>
          value
            ? String(value).slice(0, 80) +
              (String(value).length > 80 ? "…" : "")
            : emptyCell,
      },
      {
        name: t("REPORT_DATE"),
        key: "reported_at",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("reported_at"),
        filterable: true,
        render: (value) => formatReportDateTime(value),
      },
      {
        name: t("ACTIONS"),
        key: "actions",
        filterable: false,
        render: (value, rowData) => getActions(rowData),
      },
    ],
    [t, currentSystem, getActions, getLocalizedText]
  );

  const filterFields = useMemo(
    () => [
      {
        name: "pilgrim_id",
        label: t("PILGRIM_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "resolved_at",
        label: t("REPORT_STATUS"),
        type: "select",
        placeholder: t("CHOOSE"),
        options: [
          { value: "true", label: t("RESOLVED") },
          { value: "false", label: t("UNRESOLVED") },
        ],
        colSize: { md: 6 },
      },
      {
        name: "from_date",
        label: t("FROM_DATE2"),
        type: "date",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "to_date",
        label: t("TO_DATE"),
        type: "date",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "incident_number",
        label: t("REPORT_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
    ],
    [t]
  );

  return (
    <div>
      <PageHeader title={t("HOUSING_INSPECTION_REPORTS")} sticky={false} />
      <Container fluid>
        <TableC
          data={reports}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t("REPORTS_SEARCH_PLACEHOLDER")}
          pagination={
            total > 0
              ? {
                  current: meta?.current_page ?? currentPage,
                  total,
                  pageSize: meta?.per_page ?? PAGE_SIZE,
                  onChange: handlePageChange,
                }
              : undefined
          }
          actions={getActions}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          emptyStateTitle={debouncedValue ? t("NO_RESULTS") : t("NO_DATA")}
          emptyStateDescription={
            debouncedValue
              ? t("NO_RESULTS_DESCRIPTION")
              : t("NO_DATA_DESCRIPTION")
          }
        />
      </Container>

      <ConfirmModal
        isOpen={endCaseModalState.isOpen}
        onToggle={handleEndCaseCancel}
        onConfirm={handleEndCaseConfirm}
        title={t("CONFIRM")}
        message={t("CONFIRM_END_CASE_MESSAGE")}
        confirmText={t("END_CASE")}
        cancelText={t("CANCEL")}
      />
    </div>
  );
};

export default BuildingsNotInspected;
